import { makeStyles } from '@material-ui/core';
import GroupIcon from '@material-ui/icons/GroupOutlined';
import {
  CompoundEntityRef,
  Entity,
  parseEntityRef,
  RELATION_HAS_PART,
  RELATION_OWNED_BY,
  RELATION_PART_OF,
} from '@backstage/catalog-model';
import {
  getEntityRelations,
  useEntity,
  useEntityPresentation,
} from '@backstage/plugin-catalog-react';
import {
  EntityDisplayLinks,
  readableName,
} from '../../shared/EntityDisplayLink';
import { useByxT } from '../../shared/translations';
import { byxScheme } from '../../theme/tokens';
import { ByxCard } from '../ByxCard';
import { ByxCardBoundary } from '../ByxCardBoundary';
import { isSystem } from '../entityKinds';
import {
  RelationColumns,
  RelationPanels,
  RelationSection,
} from '../RelationPanels';
import { tagsOf, useAboutStyles } from './AboutCard';

/**
 * The Sistema page (Story 4.2, AC-9): Partes do sistema in the main column
 * — the system's `hasPart` relations, one table panel per kind — and Sobre
 * in the side column (description, Dono, Domínio, tags).
 */

/** The kinds of Partes do sistema, in order. */
export const SYSTEM_SECTIONS: ReadonlyArray<RelationSection> = [
  {
    id: 'components',
    relation: RELATION_HAS_PART,
    kind: 'component',
    titleKey: 'entity.squad.components',
  },
  {
    id: 'apis',
    relation: RELATION_HAS_PART,
    kind: 'api',
    titleKey: 'entity.squad.apis',
  },
  {
    id: 'resources',
    relation: RELATION_HAS_PART,
    kind: 'resource',
    titleKey: 'entity.squad.resources',
  },
];

/** Column plans of the UI spec: main column, and full width (no Sobre). */
export const SYSTEM_COLUMNS: Record<'main' | 'full', RelationColumns> = {
  main: { name: 280, owner: 140, type: 96, lifecycle: 110 },
  full: { name: 320, owner: 180, type: 120, lifecycle: 130 },
};

/**
 * The system's domain: `spec.domain`, else its `partOf` domain relation.
 * Shown as plain text (the UX gives it no action).
 */
export function systemDomain(entity: Entity): CompoundEntityRef | undefined {
  const declared = entity.spec?.domain;
  if (typeof declared === 'string' && declared.trim()) {
    try {
      return parseEntityRef(declared.trim(), {
        defaultKind: 'domain',
        defaultNamespace: entity.metadata.namespace ?? 'default',
      });
    } catch {
      return undefined;
    }
  }
  return getEntityRelations(entity, RELATION_PART_OF, { kind: 'domain' })[0];
}

/** True when Sobre has a block: description, Dono, Domínio or tags. */
export function hasSystemAbout(entity: Entity): boolean {
  return (
    isSystem(entity) &&
    (!!entity.metadata.description?.trim() ||
      getEntityRelations(entity, RELATION_OWNED_BY).length > 0 ||
      !!systemDomain(entity) ||
      tagsOf(entity).length > 0)
  );
}

const useStyles = makeStyles(theme => {
  const scheme = byxScheme(theme.palette.type);
  return {
    fields: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(2),
      margin: 0,
    },
    owner: {
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      minWidth: 0,
    },
    groupIcon: {
      flex: 'none',
      fontSize: 16,
      color: scheme.mutedText,
    },
  };
});

function SystemPartsBody() {
  const { entity } = useEntity();
  const { t } = useByxT();
  const columns = hasSystemAbout(entity)
    ? SYSTEM_COLUMNS.main
    : SYSTEM_COLUMNS.full;
  return (
    <RelationPanels
      entity={entity}
      title={t('entity.system.parts')}
      sections={SYSTEM_SECTIONS}
      columns={columns}
      pageStates={{
        error: t('entity.system.error'),
        empty: t('entity.system.empty'),
      }}
    />
  );
}

/** Partes do sistema (UI spec): one table panel per kind, no action. */
export function SystemPartsCard() {
  const { t } = useByxT();
  return (
    <ByxCardBoundary title={t('entity.system.parts')} variant="table">
      <SystemPartsBody />
    </ByxCardBoundary>
  );
}

/** The domain's display name, as plain text. */
function DomainName({ entityRef }: Readonly<{ entityRef: CompoundEntityRef }>) {
  const { primaryTitle } = useEntityPresentation(entityRef, {
    defaultKind: 'domain',
  });
  const name = readableName(primaryTitle, entityRef);
  return <span title={name}>{name}</span>;
}

function SystemAboutBody() {
  const { entity } = useEntity();
  const { t } = useByxT();
  const about = useAboutStyles();
  const classes = useStyles();
  const description = entity.metadata.description?.trim();
  const owners = getEntityRelations(entity, RELATION_OWNED_BY);
  const domain = systemDomain(entity);
  const tags = tagsOf(entity);
  if (!hasSystemAbout(entity)) {
    return null;
  }
  return (
    <ByxCard title={t('entity.about.title')}>
      <div className={about.body}>
        {description && <p className={about.description}>{description}</p>}
        {(owners.length > 0 || domain) && (
          <dl className={classes.fields}>
            {owners.length > 0 && (
              <div className={about.field}>
                <dt className={about.label}>{t('entity.about.owner')}</dt>
                <dd className={about.value}>
                  <span className={classes.owner}>
                    <GroupIcon aria-hidden className={classes.groupIcon} />
                    <EntityDisplayLinks
                      entityRefs={owners}
                      defaultKind="group"
                    />
                  </span>
                </dd>
              </div>
            )}
            {domain && (
              <div className={about.field}>
                <dt className={about.label}>{t('entity.system.domain')}</dt>
                <dd className={about.value}>
                  <DomainName entityRef={domain} />
                </dd>
              </div>
            )}
          </dl>
        )}
        {tags.length > 0 && (
          <ul className={about.tags} aria-label={t('entity.about.tags')}>
            {tags.map(tag => (
              <li key={tag} className={about.tag}>
                {tag}
              </li>
            ))}
          </ul>
        )}
      </div>
    </ByxCard>
  );
}

/**
 * Sobre of a system (UI spec): the description, Dono, Domínio and the tags,
 * one field per row; a block with no value is not rendered, and the card
 * is hidden (its filter) when there is none.
 */
export function SystemAboutCard() {
  const { t } = useByxT();
  return (
    <ByxCardBoundary title={t('entity.about.title')}>
      <SystemAboutBody />
    </ByxCardBoundary>
  );
}
